import ContactService from "@/services/contact.service.js";
import ContactToAccessService from "@/services/contact2access-group2portfolio.service.js";
import Contact from "@/models/Contact.js";
import Formatter from "@/functions/Formatter.js";
import Permissions from "@/UserPermissions/PermissionGroups";
import userPermissionsToPortfolioService from "@/services/userPermissionsToPortfolio.service";

const state = {
  contacts: [],
  permissions: [],
};

const mutations = {
  SET_CONTACTS(state, contacts) {
    state.contacts = contacts;
  },
  SET_PERMISSIONS(state, permissions) {
    state.permissions = permissions;
  },
  ADD_CONTACT_PERMISSIONS(state, permissions) {
    state.permissions.push(permissions);
  },
  SET_CONTACT_PERMISSIONS(state, permissions) {
    let permsIndex = state.permissions.findIndex(
      (p) => p.contact.id == permissions.contact.id
    );
    state.permissions[permsIndex] = permissions;
  },
  ADD_CONTACT(state, contact) {
    state.contacts.push(contact);
  },
  EDIT_CONTACT(state, contact) {
    let contactIndex = state.contacts.findIndex((c) => c.id == contact.id);
    state.contacts[contactIndex] = contact;
  },
  DELETE_CONTACT(state, contact) {
    state.contacts = state.contacts.filter((c) => c.id != contact.id);
  },
};

const actions = {
  async fetchContacts({ commit }, portfolioId) {
    try {
      const contactsToAccess = await ContactToAccessService.getAllWherePortfolio(
        portfolioId,
        false
      );
      const contactPermissions = await userPermissionsToPortfolioService.getAll(
        portfolioId
      );
      if (contactsToAccess.data) {
        let contacts = Formatter.formatContacts(contactsToAccess.data);
        commit("SET_CONTACTS", contacts);
        commit("SET_PERMISSIONS", contactPermissions.data.userPermissions);
      } else {
        console.log("Could not fetch contacts " + contactsToAccess);
      }
    } catch (error) {
      console.log(error.response ? error.response.status : error);
    }
  },
  async createContact({ commit, getters }, newData = {}) {
    let contact = new Contact();
    if (newData?.userId) {
      const userData = JSON.parse(localStorage.getItem("user")).user;
      contact.user_id = newData.userId;
      contact.has_user = true;
      contact.first_name = userData.first_name;
      contact.last_name = userData.last_name;
    }

    contact.created_by_contact = getters.getCurrentContact?.id ?? 0;
    try {
      const response = await ContactService.add(contact);
      if (response.data) {
        const portfId = newData.useMyPortfolioId
          ? newData.portfolioId
          : getters.getActivePortfolio?.id ?? newData.portfolioId;
        const contactToAccess = await ContactToAccessService.add({
          contact: response.data,
          portfolio_id: portfId,
          is_owner: newData?.isOwner ? newData.isOwner : false,
          access_groups: newData?.isImportant
            ? [8]
            : [getters.getDefaultAccessGroup ?? 3],
        });
        if (newData) {
          let perms = {
            portfolio: {
              id: portfId,
            },
            contact: {
              id: response.data.id,
            },
            permissions: newData.isOwner
              ? Permissions.PermissionGroups.Owner.Permissions
              : Permissions.PermissionGroups.Guest.Permissions,
          };

          const permsResponse = await userPermissionsToPortfolioService.add(
            perms
          );

          if (!newData.isOwner)
            commit("ADD_CONTACT_PERMISSIONS", permsResponse.data);
        }
        contact = Formatter.formatContact(response.data, contactToAccess.data);
        commit("ADD_CONTACT", contact);
        return contact;
      } else {
        console.log("could not create new contact " + response);
      }
    } catch (error) {
      console.log(error.response ? error.response.status : error);
    }
    return null;
  },
  async updateContact({ commit }, contact) {
    if (!contact || !contact.id) return;

    try {
      const contactToaccess = await ContactToAccessService.getOne(
        contact.contactToAccessId
      );
      if (contactToaccess.data) {
        contactToaccess.data.access_groups = contact.access_groups;
        ContactToAccessService.update(contactToaccess.data);
        ContactService.update(contact);
        commit("EDIT_CONTACT", contact);

        return true;
      } else {
        console.log("could not get contact to access" + contactToaccess);
      }
    } catch (error) {
      console.log(error.response ? error.response.status : error);
    }

    return false;
  },
  async deleteContact({ commit }, contact) {
    if (!contact) return;

    try {
      const deleteContactToAccessResponse = await ContactToAccessService.delete(
        contact.contactToAccessId
      );
      if (!deleteContactToAccessResponse.data) {
        console.log(
          "could not delete contact to access: " + deleteContactToAccessResponse
        );
        return false;
      }

      const deleteContactResponse = await ContactService.delete(contact.id);
      if (!deleteContactResponse.data) {
        console.log("could not delete contact: " + deleteContactResponse);
        return false;
      }

      commit("DELETE_CONTACT", contact);
      return true;
    } catch (error) {
      console.log(error.response ? error.response.status : error);
    }

    return false;
  },
  updateContactPermissions({ commit }, contactPermissions) {
    commit("SET_CONTACT_PERMISSIONS", contactPermissions);
  },
  clearContacts({ commit }) {
    commit("SET_CONTACTS", []);
  },
};

const getters = {
  getContacts: () => {
    return state.contacts;
  },
  getContactsByCategory: (state) => (categoryId) => {
    categoryId = parseInt(categoryId);
    return state.contacts.filter(function(contact) {
      return contact.categories.indexOf(categoryId) > -1;
    });
  },
  getOwner: (state) => {
    return state.contacts.find((c) => c.is_owner === true);
  },
  getContactById: (state) => (contactId) => {
    return state.contacts.find((c) => c.id == contactId);
  },
  getContactByUserId: (state) => (userId) => {
    return state.contacts.find((c) => c.user_id == userId);
  },
  getCurrentContact: (state, getters) => {
    try {
      const currentUserId = getters.getUser.id;
      return state.contacts.find((c) => c.user_id == currentUserId);
    } catch (e) {
      return;
    }
  },
  getPermissionsForContact: (state) => (contactId) => {
    return state.permissions.find((p) => p.contact.id == contactId);
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
